/**
 * @file Handles date-related operations.
 * */
import { hyphenatedCurrentLocale as locale } from '@@/bits/current_locale'

/**
 * Calculates the time difference between two dates in days.
 *
 * @param earlierDate Should be earlier than laterDate
 * @param laterDate Should be later than earlierDate
 * @param round Set to 'down' by default, can be set to 'up' and 'none'
 * @returns The day difference in milliseconds
 */
function dayDifference(earlierDate: Date, laterDate: Date, round = 'down'): number {
  const differenceInDays = (laterDate.getTime() - earlierDate.getTime()) / (1000 * 3600 * 24)

  if (round === 'down') {
    return Math.floor(differenceInDays)
  } else if (round === 'up') {
    return Math.ceil(differenceInDays)
  } else {
    return differenceInDays
  }
}

/**
 * Adds the days specified to the date provided and return the resultant date.
 * Can supply a negative number for days to minus instead.
 *
 * @param date The date to add days to
 * @param days The days to add to the date supplied
 * @returns The new date after the days are added
 */
function addDays(date: Date, days: number): Date {
  const newDate = new Date(date)
  newDate.setDate(date.getDate() + days)
  return newDate
}

/**
 * Returns a pretty version of the current date.
 * E.g "October 21, 2015"
 *
 * @returns a pretty version of the current date.
 */
function getPrettyCurrentDate(): string {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as const
  return new Intl.DateTimeFormat(locale, options).format(new Date())
}

/**
 * Returns a pretty version of the date parameter.
 *
 * @param  {string} date: Eg: '2022-04-26T05:21:30.185Z'
 * @param  {Intl.DateTimeFormatOptions={year:'numeric'}} options
 * @returns string: Eg: 'April 26, 2022'
 */
function formatDate(
  date: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
): string {
  return new Intl.DateTimeFormat(locale, options).format(new Date(date))
}

/**
 * Returns whether two dates belong to the same day.
 *
 * @param {Date} date1 The first date
 * @param {Date} date2 The second date
 * @returns {boolean} Whether the two dates belong to the same day.
 */
function sameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

// Adapted from https://gist.github.com/WebReflection/6076a40777b65c397b2b9b97247520f0
/**
 * Returns the date in the local timezone as a string for datetimepicker inputs.
 * @param {Date} date The date to convert
 * @returns {string} The date in the local timezone as a string.
 */
function toLocalDatetimeString(date: Date): string {
  const ten = function (i: number): string {
    return (i < 10 ? '0' : '') + i.toString()
  }
  const YYYY = date.getFullYear().toString()
  const MM = ten(date.getMonth() + 1)
  const DD = ten(date.getDate())
  const HH = ten(date.getHours())
  const II = ten(date.getMinutes())
  return YYYY + '-' + MM + '-' + DD + 'T' + HH + ':' + II
}

export { addDays, dayDifference, formatDate, getPrettyCurrentDate, sameDay, toLocalDatetimeString }
